import React, { useMemo, useState } from "react";
import { Alert, Badge, Button, Form, InputGroup, Modal, Spinner, Table } from "react-bootstrap";
import { Flag, Search, X } from "react-feather";
import { formatDistanceToNow } from "date-fns";
import { apiErrorMessage, useGet, usePost } from "../../api";
import { IWorkPipelineUrgent, IWorkPipelineUrgentBody } from "../../interfaces";

const URGENT_EXPLAINER =
  "Urgent domains sit at the top of every bucket on the Work Pipeline and Template Pipeline, and in their Urgent tabs, until a lead takes the flag off.";

const urgentTitle = (u: IWorkPipelineUrgent) =>
  [
    u.note,
    `Marked urgent by ${u.setByName ?? "a lead"} ${formatDistanceToNow(new Date(u.setAt), { addSuffix: true })}`,
  ]
    .filter(Boolean)
    .join("\n");

interface IUrgentTarget {
  domain: string;
  countryCode: string;
}

/** Badge for urgent rows; leads also get "Mark urgent" on rows that aren't. */
export const UrgentToggle: React.FC<
  IUrgentTarget & {
    urgent: IWorkPipelineUrgent | null | undefined;
    canEdit: boolean;
    onChanged: () => void;
    className?: string;
  }
> = ({ domain, countryCode, urgent, canEdit, onChanged, className }) => {
  const [marking, setMarking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clearReq = usePost();

  const clear = async () => {
    setError(null);
    try {
      await clearReq.execute("work-pipeline/urgent/off", { domain, countryCode });
      onChanged();
    } catch (e) {
      setError(apiErrorMessage(e, "Could not clear it."));
    }
  };

  if (!urgent && !canEdit) return null;
  return (
    <span className={className}>
      {urgent ? (
        <Badge bg="danger" className="d-inline-flex align-items-center gap-1" title={urgentTitle(urgent)}>
          <Flag size={11} /> Urgent
          {canEdit && (
            <button
              type="button"
              className="btn btn-link p-0 text-white lh-1"
              disabled={clearReq.loading}
              onClick={(e) => {
                e.stopPropagation();
                clear();
              }}
              title="No longer urgent: take the flag off so it drops back to its normal place"
            >
              {clearReq.loading ? <Spinner animation="border" size="sm" /> : <X size={11} />}
            </button>
          )}
        </Badge>
      ) : (
        <Button
          variant="link"
          size="sm"
          className="p-0 text-danger text-decoration-none small"
          onClick={(e) => {
            e.stopPropagation();
            setMarking(true);
          }}
          title={URGENT_EXPLAINER}
        >
          <Flag size={11} className="me-1" />
          Mark urgent
        </Button>
      )}
      {error && <small className="text-danger d-block">{error}</small>}
      <MarkUrgentModal
        show={marking}
        items={[{ domain, countryCode }]}
        onHide={() => setMarking(false)}
        onDone={() => {
          setMarking(false);
          onChanged();
        }}
      />
    </span>
  );
};

/** Asks why it is urgent, then flags one or more domain + geo pairs. */
export const MarkUrgentModal: React.FC<{
  show: boolean;
  items: IUrgentTarget[];
  onHide: () => void;
  onDone: () => void;
}> = ({ show, items, onHide, onDone }) => {
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const req = usePost<{ flagged: number }, IWorkPipelineUrgentBody>();

  const submit = async () => {
    setError(null);
    try {
      await req.execute("work-pipeline/urgent", { items, note: note.trim() });
      setNote("");
      onDone();
    } catch (e) {
      setError(apiErrorMessage(e, "Could not mark it urgent."));
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered onClick={(e: React.MouseEvent) => e.stopPropagation()}>
      <Modal.Header closeButton>
        <Modal.Title as="h5">
          Mark {items.length === 1 ? `${items[0].domain} · ${items[0].countryCode}` : `${items.length} domains`} urgent
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="small text-muted">{URGENT_EXPLAINER}</p>
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}
        <Form.Label className="small fw-semibold">Why is it urgent? (optional)</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          maxLength={1000}
          value={note}
          placeholder="e.g. Repeat offender on the 50% failure list, or: Tarryn needs videos by Friday"
          onChange={(e) => setNote(e.target.value)}
        />
        <Form.Text>Shown to testers and devs when they hover the Urgent badge.</Form.Text>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="danger" onClick={submit} disabled={req.loading || !items.length}>
          {req.loading ? <Spinner animation="border" size="sm" /> : "Mark urgent"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const parseLines = (text: string) => {
  const items: IUrgentTarget[] = [];
  const bad: string[] = [];
  for (const line of text.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const [domain, countryCode] = trimmed.split(/[\s,;]+/);
    if (domain && countryCode && /^[a-z]{2,3}$/i.test(countryCode)) {
      items.push({ domain, countryCode: countryCode.toUpperCase() });
    } else {
      bad.push(trimmed);
    }
  }
  return { items, bad };
};

/** Every urgent flag in one place, including domains not on either board yet. */
export const ManageUrgentModal: React.FC<{
  show: boolean;
  canEdit: boolean;
  onHide: () => void;
  onChanged: () => void;
}> = ({ show, canEdit, onHide, onChanged }) => {
  const { data, loading, refetch } = useGet<IWorkPipelineUrgent[]>(show ? "work-pipeline/urgent" : null);
  const [search, setSearch] = useState("");
  const [paste, setPaste] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const addReq = usePost<{ flagged: number }, IWorkPipelineUrgentBody>();
  const parsed = useMemo(() => parseLines(paste), [paste]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data ?? []).filter(
      (u) => !q || u.domain.includes(q) || u.countryCode.toLowerCase() === q
    );
  }, [data, search]);

  const changed = () => {
    refetch().catch(() => undefined);
    onChanged();
  };

  const add = async () => {
    setError(null);
    try {
      await addReq.execute("work-pipeline/urgent", { items: parsed.items, note: note.trim() });
      setPaste("");
      setNote("");
      changed();
    } catch (e) {
      setError(apiErrorMessage(e, "Could not mark them urgent."));
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">
          <Flag size={16} className="me-2 text-danger" />
          Urgent domains
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="small text-muted">{URGENT_EXPLAINER}</p>
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}

        <div className="d-flex justify-content-between align-items-center mb-2 gap-2">
          <h6 className="mb-0">Marked urgent ({data?.length ?? 0})</h6>
          <InputGroup size="sm" style={{ maxWidth: 260 }}>
            <InputGroup.Text>
              <Search size={12} />
            </InputGroup.Text>
            <Form.Control
              value={search}
              placeholder="Search domain or country"
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
        </div>
        <div style={{ maxHeight: 260, overflow: "auto" }} className="mb-3">
          {loading && !data ? (
            <Spinner animation="border" size="sm" />
          ) : !shown.length ? (
            <div className="small text-muted">
              {search.trim() ? "No urgent domain matches." : "Nothing is marked urgent."}
            </div>
          ) : (
            <Table size="sm" className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Country</th>
                  <th>Why</th>
                  <th>By</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {shown.map((u) => (
                  <tr key={`${u.domain}|${u.countryCode}`}>
                    <td className="small">{u.domain}</td>
                    <td className="small">{u.countryCode}</td>
                    <td className="small text-muted">{u.note ?? "—"}</td>
                    <td className="small text-muted text-nowrap">
                      {u.setByName ?? "—"} · {formatDistanceToNow(new Date(u.setAt), { addSuffix: true })}
                    </td>
                    <td className="text-end">
                      <UrgentToggle
                        domain={u.domain}
                        countryCode={u.countryCode}
                        urgent={u}
                        canEdit={canEdit}
                        onChanged={changed}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>

        {canEdit && (
          <>
            <h6 className="mb-2">Add domains</h6>
            <Form.Control
              as="textarea"
              rows={4}
              className="font-monospace small"
              value={paste}
              placeholder={"One per line: domain, country\npriceline.com, US\nbasspro.com, US"}
              onChange={(e) => setPaste(e.target.value)}
            />
            {parsed.bad.length > 0 && (
              <Form.Text className="text-danger d-block">
                Needs a domain and a country code: {parsed.bad.slice(0, 3).join("; ")}
                {parsed.bad.length > 3 ? ` and ${parsed.bad.length - 3} more` : ""}
              </Form.Text>
            )}
            <Form.Control
              size="sm"
              className="mt-2"
              maxLength={1000}
              value={note}
              placeholder="Why they are urgent (optional, applies to every line)"
              onChange={(e) => setNote(e.target.value)}
            />
            <Button
              variant="danger"
              size="sm"
              className="mt-2"
              disabled={addReq.loading || !parsed.items.length}
              onClick={add}
              title="Flags every line above; re-flagging a domain replaces its note"
            >
              {addReq.loading ? (
                <Spinner animation="border" size="sm" />
              ) : parsed.items.length ? (
                `Mark ${parsed.items.length} urgent`
              ) : (
                "Mark urgent"
              )}
            </Button>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
